import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http';

import { BaseResponse } from './BaseResponse';
import { environment } from 'src/environments/environment';
import { L10nService } from '../l10n.service';
import { NotificationService } from '../notificaton.service';

@Injectable()
export class DataService {
    private headers: HttpHeaders;
    private baseUrl: string;
    private serverUrl: string;
    private throttledRequests = new Map<string,Promise<Object>>();
    private cache: Map<string, { timestamp: number, data: any }> = new Map();

    constructor(private http: HttpClient, l10nService: L10nService, private notifier: NotificationService) {
        this.baseUrl = environment.baseUrl + l10nService.getCurrentLang();
        this.serverUrl = environment.baseUrl.replace(/\/+$/, "");
        console.log(this.baseUrl);
        let headers = { 'Content-Type': 'application/json' };
        this.headers = new HttpHeaders(headers);
    }

    public get<T>(url: string, throttle: boolean = false, cacheDurationMs: number = 0): Promise<T> {
        let promise;
        url = this.removeUndefinedFromUrl(url);
    
        if (cacheDurationMs > 0) {
            const cachedData = this.getFromCache<T>(url, cacheDurationMs);
            if (cachedData) {
                return Promise.resolve(cachedData);
            }
        }
    
        if (throttle) {
            promise = this.throttledRequests.get(url);
            /*if (promise) {
                console.log('Throttling ' + url);
            }*/
        }
    
        if (!promise) {
            promise = this.http
                .get(this.baseUrl + '/' + url, { headers: this.headers })
                .toPromise();
    
            if (throttle) {
                this.throttledRequests.set(url, promise);
            }
        }
    
        return promise.then(res => {
            this.throttledRequests.delete(url);
            let response = res as BaseResponse;
    
            if (!response) {
                throw new Error("Error");
            }
    
            if (response.error) {
                if (response.showAsWarning) {
                    this.notifier.showWarning(response.error);
                } else {
                    throw new Error(response.error);
                }
            }
    
            if (cacheDurationMs > 0) {
                this.saveToCache(url, response.data as T, cacheDurationMs);
            }
    
            return Promise.resolve(response.data as T);
        }).catch(ex => {
            this.notifier.showError(this.extractErrorMessage(ex));
            throw ex;
        });
    }
    
    private getFromCache<T>(url: string, cacheDurationMs: number): T | null {
        const cached = this.cache.get(url);
        if (cached && Date.now() - cached.timestamp < cacheDurationMs) {
            console.log('Returning cached data for', url);
            return cached.data as T;
        }
        return null;
    }
    
    private saveToCache<T>(url: string, data: T, cacheDurationMs: number): void {
        const cacheEntry = { timestamp: Date.now(), data };
        this.cache.set(url, cacheEntry);
    
        setTimeout(() => {
            this.cache.delete(url);
        }, cacheDurationMs);
    }
    
    private removeUndefinedFromUrl(url: string): string {
        const urlParts = url.split('?');
        if (urlParts.length !== 2) {
            return url;
        }
      
        const queryString = urlParts[1];
        const params = new URLSearchParams(queryString);
      
        const validParams: [string, string][] = [];
      
        params.forEach((value, key) => {
            if (value !== 'undefined') {
                validParams.push([key, value]);
            }
        });
      
        const updatedQueryString = validParams.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join('&');
      
        if (updatedQueryString === '') {
            return urlParts[0];
        }
      
        return `${urlParts[0]}?${updatedQueryString}`;
    }
    

    public post<T>(url: string, body: any): Promise<T> {
        Object.keys(body).forEach(key => body[key] === undefined ? delete body[key] : {});
        return this.http
            .post(this.baseUrl + '/' + url, body, { headers: this.headers })
            .toPromise().then(
            res => {
                let response = res as BaseResponse;
                if (response.error) {
                    if (response.showAsWarning) {
                        this.notifier.showWarning(response.error);
                    } else {
                        const err: any = new Error(response.error);
                        if (response.skipErrorPopup) {
                            err.skipErrorPopup = true;
                        }
                        throw err;
                    }
                }
                return Promise.resolve(response.data as T);
            }).catch(ex => {
                if (!ex.skipErrorPopup) {
                    this.notifier.showError(this.extractErrorMessage(ex));
                }
                throw ex;
            });
    }

    public delete<T>(url: string): Promise<T> {
        return this.http
            .delete(this.baseUrl + '/' + url, { headers: this.headers })
            .toPromise().then(
                res => {
                    let response = res as BaseResponse;
                    if (response.error) {
                        throw new Error(response.error);
                    }
                    return Promise.resolve(response.data as T);
                }).catch(ex => {
                    this.notifier.showError(this.extractErrorMessage(ex));
                    throw ex;
                });
    }

    public postFile<T>(url: string, fileToUpload: File, frmData: any = null, onProgress?: (progress: number) => void): Promise<T> {
        const formData: FormData = new FormData();
        formData.append('file', fileToUpload, fileToUpload.name);
        if (frmData) {
            for (let key in frmData) {
                formData.append(key, frmData[key]);
            }
        }

        return new Promise<T>((resolve, reject) => {
            this.http.post(this.baseUrl + '/' + url, formData, {
                reportProgress: true,
                observe: 'events'
            }).subscribe({
                next: (event) => {
                    if (event.type === HttpEventType.UploadProgress) {
                        const percentDone = Math.round((event.loaded / (event.total ?? 1)) * 100);
                        if (onProgress) {
                            onProgress(percentDone);
                        }
                    } else if (event.type === HttpEventType.Response) {
                        const response = event.body as unknown as BaseResponse;
                        if (response.error) {
                            reject(new Error(response.error));
                        } else {
                            resolve(response.data as T);
                        }
                    }
                },
                error: (error) => {
                    this.notifier.showError(this.extractErrorMessage(error));
                    reject(error);
                }
            });
        });
    }

    public getCookieValue(a) {
        var b = document.cookie.match('(^|[^;]+)\\s*' + a + '\\s*=\\s*([^;]+)');
        return b ? b.pop() : '';
    }

    private extractErrorMessage(errorObj: any) {
        let possibleProperties = ['error', 'statusText', 'message'];
        for(let i = 0; i < possibleProperties.length; ++i) {
            let possibleMsg = errorObj[possibleProperties[i]];
            if (possibleMsg && (typeof possibleMsg === 'string' || possibleMsg instanceof String)) {
                return possibleMsg;
            }
        }
        return errorObj;
    }

    public getBaseUrl(): string {
        return this.baseUrl;
    }

    public getServerUrl(): string {
        return this.serverUrl;
    }
}
